import React, { Component } from 'react';
import PropTypes from 'prop-types';

// this input is a filterable select component where the user can type in a search string.
// Fundamentally, it operates the same as BaseSelect, including styling. 
export default class FilterableSelect extends Component {
  constructor(props) {
    super(props);

    const { value } = props;

    this.state = {
      value: value,
      cursor: -1,
      uiValue: null,
      isValid: Boolean(value), // any option selected is valid
      isTouched: false,
      isFocused: false,
      userQuery: '',
    };

    this.handleClick = this.handleClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }
  
  componentDidMount() {
    const { optionData } = this.props;
    
    this.valueTextMap = optionData.reduce((result, option) => {
      const { text, value } = option;
      result[value] = text;
      return result;
    }, {});

    this.setState(({ value }) => ({
      uiValue: this.valueTextMap[value],
    }));
  }
  
  componentDidUpdate = ({ optionData: oldOptionData }, { value: prevValue, uiValue: prevUiValue, isFocused: wasFocused }) => {
    const { isFocused } = this.state;
    const { optionData } = this.props;

    if (!wasFocused && isFocused) {
      document.addEventListener('mousedown', this.handleClick);
    } else if (wasFocused && !isFocused) {
      document.removeEventListener('mousedown', this.handleClick);
    }

    // Update valueTextMap if the option data changes. The value set depends on if it's the first load or not.
    if ((Array.isArray(optionData) && prevValue && !prevUiValue) || JSON.stringify(oldOptionData) !== JSON.stringify(optionData)) {
      this.valueTextMap = optionData.reduce((result, option) => {
        const { text, value } = option;
        result[value] = text;
        return result;
      }, {});

      this.setState(({ value }) => ({
        value: prevValue && !prevUiValue ? '' : value,
        uiValue: this.valueTextMap[value],
        isValid: false,
        isTouched: false,
        isFocused: false,
      }));
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClick);
  }

  handleBlur = () => {
    this.hiddenInput.blur();
    this.setState({ isTouched: true, isFocused: false });
  }

  handleFocus = () => {
    this.hiddenInput.focus();
    this.setState({ isFocused: true, value: '', uiValue: '', isValid: false });
  }

  handleChange = (value, e) => {
    e && e.stopPropagation();

    const { inputKey, eventHandler } = this.props;
    const isValid = Boolean(value);
    const uiValue = this.valueTextMap[value];

    eventHandler(value, inputKey, isValid);
    this.setState({
      isFocused: false,
      isValid,
      uiValue,
      value,
    });
  }

  handleKeyDown = (e) => {
    const { keyCode } = e;
    const { cursor } = this.state;
    const { optionData } = this.props;

    // do not allow arrow key if already hovering to prevent to active options
    if (!this.hoveringOption) {
      // "up"
      if (keyCode === 38 && cursor >= 0) {
        e.preventDefault();
        this.setState((prevState) => ({
          cursor: prevState.cursor - 1,
        }));
        // "down"
      } else if (keyCode === 40 && cursor < optionData.length - 1) {
        e.preventDefault();
        this.setState((prevState) => ({
          cursor: prevState.cursor + 1,
        }));
        // "enter"
      } else if (keyCode === 13 && cursor >= 0 && optionData.length > 0) {
        e.preventDefault();
        const { value = '' } = optionData[cursor];

        this.handleBlur();
        this.handleChange(value, e);
      }
    }
  }

  handleOptionHover = () => {
    if (!this.hoveringOption) this.hoveringOption = true;

    if (this.state.cursor >= 0) {
      this.setState({ cursor: -1 });
    }
  }

  handleOptionLeave = () => {
    if (this.hoveringOption) this.hoveringOption = false;
  }

  getOptions = () => {
    const {
      optionData,
      optionStyles,
      optionClassNames,
    } = this.props;

    const { cursor, userQuery } = this.state;
    let isOptionActive = false;

    const options = optionData.reduce((result, { key, text, value }, i) => {
      const isActiveOption = cursor === i;
      const optionState = isActiveOption ? '--active' : '';

      if (text.toLowerCase().includes(userQuery ? userQuery.toLowerCase() : '')) result.push(
        <li
          onClick={(e) => this.handleChange(value, e)}
          className={`base-select__option${optionState} ${optionClassNames}`}
          ref={(option) => {
            if (isActiveOption) {
              isOptionActive = true;
              this.activeOption = option;
            }
          }}
          onMouseEnter={this.handleOptionHover}
          onMouseLeave={this.handleOptionLeave}
          style={optionStyles}
          key={key}
        >
          {text}
        </li>
      );

      return result;
    }, []);

    if (!isOptionActive) {
      this.activeOption = null;
    }

    return options;
  }

  handleXDelete = () => {
    this.handleFocus();
    this.handleChange('');
  }

  handleArrow = (e) => {
    e.stopPropagation();

    const { isFocused } = this.state;
    this.setState({ isFocused: !isFocused });
  }

  handleUserQuery = ({ target: { value } }) => {
    const { optionData } = this.props;
    const regex = new RegExp(value, 'gi');
    const cursor = optionData.findIndex(({ text }) => regex.test(text));

    this.setState({ userQuery: value, cursor }, () => {
      if (this.activeOption) {
        const { offsetTop } = this.activeOption;
        this.dropDown.scrollTop = offsetTop - 56;
      }
    });
  }

  handleClick({ target }) {
    if (!this.node.contains(target)) {
      this.handleBlur();
    }
  }
  render() {
    const {
      isDisabled,
      inputStyles,
      noErrorState,
      dropDownStyles,
      xDeleteChar,
      hideXDelete,
      inputClassNames,
      outerWrapStyles,
      hideErrorMessage,
      isErrorTriggered,
      dropDownClassNames,
      outerWrapClassNames,
      requiredTextStyles,
      requiredTextClassNames,
      xDeleteClassNames,
      xDeleteStyles,
      arrowStyles,
      arrowClassNames,
      validationRules: { required },
      placeholder,
      errorMessage,
    } = this.props;

    const {
      value,
      isValid,
      uiValue,
      isTouched,
      isFocused,
      userQuery,
    } = this.state;

    const isErrorState = (!noErrorState
      && !isValid && !isFocused && isTouched)
      || (!isValid && isErrorTriggered && !isFocused);

    const options = this.getOptions();

    let outerWrapState = '';
    if (isDisabled) outerWrapState = '--disabled';
    else if (isValid && isFocused) outerWrapState = '--valid-dropdown';
    else if (isValid) outerWrapState = '--valid';
    else if (isFocused) outerWrapState = '--focused';
    else if (isErrorState) outerWrapState = '--error';

    let fullOuterWrapClassNames = `base-select__outer-wrap${outerWrapState} ${outerWrapClassNames}`;
    if (required) fullOuterWrapClassNames += ' base-select__outer-wrap--required';
    
    const isValuePresent = value && String(value).length > 0;

    return (
      <div
        style={outerWrapStyles}
        onClick={this.handleFocus}
        className={fullOuterWrapClassNames}
        ref={(node) => { this.node = node }}
      >
        {(!hideXDelete && isValuePresent) && (
          <span style={xDeleteStyles}
          onClick={this.handleXDelete}
          className={`base-select__x-delete ${xDeleteClassNames}`}
          >
            {xDeleteChar}
          </span>
        )}
        {(hideXDelete) && (
          <span
            style={arrowStyles}
            onClick={(e) => this.handleArrow(e)}
            className={`${isFocused ? `base-select__arrow--up` : `base-select__arrow--down`} ${arrowClassNames}`}
          />
        )}
        <input
          type="text"
          value={uiValue ? uiValue : userQuery}
          placeholder={placeholder}
          onKeyDown={this.handleKeyDown}
          onChange={this.handleUserQuery}
          className={`filterable-select__input ${inputClassNames}`}
          style={inputStyles}
          ref={(hiddenInput) => { this.hiddenInput = hiddenInput }}
        />
        {(!hideErrorMessage && isErrorState) && (
          <div
            style={requiredTextStyles}
            onClick={this.handleFocus}
            className={`base-select__required ${requiredTextClassNames}`}
          >
            {errorMessage}
          </div>
        )}
        {isFocused && (
          <ul
            ref={(dropDown) => { this.dropDown = dropDown }}
            style={dropDownStyles}
            className={`base-select__dropdown ${dropDownClassNames}`}
          >
            {options}
          </ul>
        )}
      </div>
    );
  }
}

FilterableSelect.propTypes = {
  requriedTextClassNames: PropTypes.string,
  hideXDelete: PropTypes.bool,
  xDeleteChar: PropTypes.string,
  xDeleteClassNames: PropTypes.string,
  xDeleteStyles: PropTypes.object,
  arrowClassNames: PropTypes.string,
  arrowStyles: PropTypes.object,
  outerWrapClassNames: PropTypes.string,
  value: PropTypes.any.isRequired,
  isDisabled: PropTypes.bool,
  inputClassNames: PropTypes.string,
  dropDownClassNames: PropTypes.string,
  optionClassNames: PropTypes.string,
  isErrorTriggered: PropTypes.bool,
  optionStyles: PropTypes.object,
  outerWrapStyles: PropTypes.object,
  dropDownStyles: PropTypes.object,
  inputStyles: PropTypes.object,
  inputKey: PropTypes.any.isRequired,
  eventHandler: PropTypes.func.isRequired,
  optionData: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.any.isRequired,
    text: PropTypes.string.isRequired,
    value: PropTypes.any.isRequired,
  })).isRequired,
  noErrorState: PropTypes.bool,
  hideErrorMessage: PropTypes.bool,
  errorMessage: PropTypes.string,
  requiredTextStyles: PropTypes.object,
  validationRules: PropTypes.object,
  placeholder: PropTypes.string,
};

FilterableSelect.defaultProps = {
  outerWrapClassNames: '',
  errorMessage: 'This field is required.',
  outerWrapStyles: {},
  inputClassNames: '',
  inputStyles: {},
  optionClassNames: '',
  optionStyles: {},
  dropDownClassNames: '',
  dropDownStyles: {},
  hideErrorMessage: false,
  isErrorTriggered: false,
  isDisabled: false,
  noErrorState: false,
  hideXDelete: false,
  xDeleteClassNames: '',
  xDeleteStyles: {},
  xDeleteChar: '✖',
  arrowStyles: {},
  arrowClassNames: '',
  requiredTextClassNames: '',
  requiredTextStyles: {},
  validationRules: {},
  placeholder: null,
}



// WEBPACK FOOTER //
// ./src/features/common/FilterableSelect.js